import { inject, NgModule, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PostHeadersInterceptor } from './interceptors/headers.interceptors';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';
import { LocalStorageOAuthStorage } from './auth/local-storage-oauth-storage';
import { OidcAuthService } from './auth/oidc-auth.service';
import { OidcCallbackComponent } from './auth/oidc-callback/oidc-callback.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { LoginModule } from './login/login.module';
import { LoginService } from './login/login.service';
import { NavbarModule } from './navbar/navbar.module';
import { IcteamGuard } from './icteam.guard';
import { AccessDeniedComponent } from './shared/access-denied/access-denied.component';
import { AccessTokenInterceptor } from './shared/access-token.interceptor';
import { ResponseInterceptor } from './shared/response.interceptor';
import { ResponseTokenInterceptor } from './shared/response-token.interceptor';
import { SharedModule } from './shared/shared.module';
import { GroupsService } from './shared/groups.service';
import { StudentsService } from './shared/students.service';

@NgModule({ declarations: [
  AppComponent,
  AccessDeniedComponent,
  OidcCallbackComponent,
],
bootstrap: [AppComponent], imports: [BrowserModule,
  BrowserAnimationsModule,
  LoginModule,
  AppRoutingModule,
  NavbarModule,
  SharedModule,
  MatIconModule,
  MatButtonModule,
  OAuthModule.forRoot(),
  ], providers: [
  // Angular 22 removed zone-based change detection from BrowserModule's providers, so an
  // NgModule app boots without a zone CD scheduler (running in the root zone, effectively
  // zoneless): HTTP responses resolve but the view never re-renders. Opt back into zone
  // change detection explicitly. Remove only when migrating to zoneless.
  provideZoneChangeDetection(),
  { provide: HTTP_INTERCEPTORS, useClass: PostHeadersInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: AccessTokenInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: ResponseInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: ResponseTokenInterceptor, multi: true },
  // Persist OIDC tokens in localStorage (shared across tabs, survives tab close) -- see
  // LocalStorageOAuthStorage. Cross-tab refresh serialization lives in OidcAuthService.
  { provide: OAuthStorage, useClass: LocalStorageOAuthStorage },
  // Configure the QLM OIDC client once at startup, then rehydrate user-info. Tokens now
  // survive a reload from localStorage, so a reload no longer passes through
  // /auth/callback (which used to fetch user-info) -- without this, the route guards'
  // hasInfo check would fail on reload and bounce the user into a silent re-login.
  // Best-effort: a revoked/expired token just falls through to /login.
  provideAppInitializer(async () => {
    const oidc = inject(OidcAuthService);
    oidc.configure();
    const loginService = inject(LoginService);
    if (loginService.isLoggedIn && !loginService.hasInfo) {
      try {
        await loginService.getUserInfo();
      } catch {
        // Guard will route to /login for (silent) re-auth.
      }
    }
  }),
  StudentsService,
  GroupsService,
  provideHttpClient(withInterceptorsFromDi())
] })
export class AppModule { }

